import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Shield, Sparkles, Wrench, Building2, Home, Phone, Mail, MapPin, Menu, X, ArrowRight, CheckCircle2, Users, Award, Target, FileCheck, Star, TrendingUp, Zap, Clock, Eye, AlertTriangle, Video, Lock, Droplet, Wind, Hammer, Leaf, Trash2, BriefcaseIcon, Bug } from 'lucide-react';

const MHIntelligence = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [currentHero, setCurrentHero] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeSection, setActiveSection] = useState('');
  const [imageLoaded, setImageLoaded] = useState(true);
  const [currentServicePage, setCurrentServicePage] = useState(null);
  const dropdownRef = useRef(null);

  // Loading animation
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 2000);
  }, []);

  // Hero rotation with pause on hover
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.querySelector('.hero-section:hover')) {
        setCurrentHero((prev) => (prev + 1) % 4);
      }
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Keyboard navigation for hero slider
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setCurrentHero((prev) => (prev === 0 ? 3 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentHero((prev) => (prev === 3 ? 0 : prev + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Image transition effect
  useEffect(() => {
    setImageLoaded(false);
    const timer = setTimeout(() => {
      setImageLoaded(true);
    }, 50);
    return () => clearTimeout(timer);
  }, [currentHero]);

  const heroSlides = [
    {
      title: "PROTECTING, MAINTAINING",
      subtitle: "AND POWERING YOUR WORLD",
      description: "100% Black-owned, Level 1 B-BBEE integrated facility management solutions",
      cta: "GET A QUOTE",
      icon: Shield
    },
    {
      title: "ELITE SECURITY",
      subtitle: "SERVICES",
      description: "PSIRA-registered professionals with advanced CCTV and surveillance systems",
      cta: "SECURE YOUR FACILITY",
      icon: Shield
    },
    {
      title: "TECHNICAL EXCELLENCE",
      subtitle: "GUARANTEED",
      description: "Electrical, mechanical, plumbing, and HVAC maintenance by qualified technicians",
      cta: "LEARN MORE",
      icon: Wrench
    },
    {
      title: "COMPREHENSIVE CLEANING",
      subtitle: "& HYGIENE",
      description: "Industry-approved methods ensuring healthy, safe, and professional environments",
      cta: "DISCOVER MORE",
      icon: Sparkles
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = document.querySelectorAll('[data-animate]');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.75) {
          setVisibleSections(prev => new Set([...prev, section.dataset.animate]));
        }
      });

      const sectionIds = ['services', 'aboutus', 'clients', 'contact'];
      for (const id of sectionIds) {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Enhanced service data with detailed information
  const servicesDetailed = [
    {
      id: 'security',
      icon: Shield,
      title: "Security Services",
      shortDescription: "PSIRA-registered professionals",
      fullDescription: "MH Intelligence provides professional security solutions designed to safeguard people, property, and assets. Our services are delivered by trained, vetted, and PSIRA-registered personnel, supported by modern surveillance technologies.",
      items: [
        {
          name: "Guarding & Access Control",
          icon: Lock,
          description: "Professional security guards and access control systems to protect your premises 24/7. Our trained personnel ensure only authorized individuals enter your facility.",
          features: ["24/7 Guard Services", "Access Control Systems", "Visitor Management", "Patrol Services", "PSIRA Registered Guards"]
        },
        {
          name: "Alarm Monitoring & Response",
          icon: AlertTriangle,
          description: "Real-time alarm monitoring and rapid response coordination to security incidents. Connected to leading control rooms for immediate action.",
          features: ["24/7 Monitoring", "Armed Response", "Panic Button Systems", "Medical Emergency Response", "Technical Support"]
        },
        {
          name: "CCTV & Surveillance Systems",
          icon: Video,
          description: "Advanced CCTV installations, maintenance, and monitoring. High-definition cameras with remote viewing capabilities and AI-powered analytics.",
          features: ["HD Camera Installation", "Remote Monitoring", "AI-Powered Analytics", "Cloud Storage", "Mobile App Access", "Night Vision Systems"]
        },
        {
          name: "Risk Assessments & Site Audits",
          icon: Eye,
          description: "Comprehensive security assessments to identify vulnerabilities and provide actionable recommendations for enhanced protection.",
          features: ["Vulnerability Analysis", "Security Audits", "Risk Mitigation Plans", "Compliance Reports", "Ongoing Reviews"]
        }
      ],
      benefits: [
        "PSIRA-registered and compliant security personnel",
        "Modern surveillance and monitoring technology",
        "Rapid response to security incidents",
        "Customized security solutions for your needs",
        "Regular training and performance evaluation"
      ],
      color: "from-red-600 to-red-700",
      gradient: "bg-gradient-to-br from-red-50 to-red-100"
    },
    {
      id: 'cleaning',
      icon: Sparkles,
      title: "Cleaning & Hygiene Services",
      shortDescription: "Professional hygiene solutions",
      fullDescription: "We offer comprehensive cleaning and hygiene services that promote healthy, safe, and professional environments. Our cleaning teams are trained in industry-approved methods, products, and safety standards to ensure hygiene compliance and operational continuity.",
      items: [
        {
          name: "Commercial & Industrial Cleaning",
          icon: Building2,
          description: "Professional cleaning services for offices, factories, and commercial spaces. Daily, weekly, or customized cleaning schedules to maintain pristine environments.",
          features: ["Office Cleaning", "Factory Floor Cleaning", "Retail Space Cleaning", "Warehouse Cleaning", "Daily Maintenance"]
        },
        {
          name: "Deep Cleaning Services",
          icon: Sparkles,
          description: "Intensive deep cleaning for thorough sanitation and hygiene. Specialized cleaning for carpets, upholstery, windows, and hard-to-reach areas.",
          features: ["Carpet Cleaning", "Upholstery Cleaning", "Window Cleaning", "Floor Polishing", "Kitchen Deep Cleaning", "Sanitization Services"]
        },
        {
          name: "Washroom Hygiene Solutions",
          icon: Droplet,
          description: "Complete washroom hygiene management including supplies, maintenance, and sanitation. Ensuring clean, safe, and compliant facilities.",
          features: ["Washroom Supplies", "Sanitizer Dispensers", "Air Fresheners", "Deep Sanitization", "Regular Maintenance", "Compliance Monitoring"]
        },
        {
          name: "Pest Control Services",
          icon: Bug,
          description: "Professional pest management to eliminate and prevent infestations. Safe, effective treatments for all types of pests.",
          features: ["Rodent Control", "Insect Control", "Bird Control", "Fumigation Services", "Prevention Programs", "Regular Inspections"]
        }
      ],
      benefits: [
        "Industry-approved cleaning methods and products",
        "Trained and supervised cleaning teams",
        "Flexible service schedules",
        "Health and safety compliance",
        "Environmentally friendly solutions available"
      ],
      color: "from-gray-800 to-gray-900",
      gradient: "bg-gradient-to-br from-gray-50 to-gray-100"
    },
    {
      id: 'technical',
      icon: Wrench,
      title: "Technical & Maintenance Services",
      shortDescription: "Expert technical support",
      fullDescription: "MH Intelligence delivers reliable technical and maintenance services to ensure optimal performance and longevity of infrastructure and equipment. We focus on preventative and corrective maintenance to reduce downtime and operational risk.",
      items: [
        {
          name: "Electrical Maintenance & Installations",
          icon: Zap,
          description: "Comprehensive electrical services including installations, repairs, and preventative maintenance by qualified electricians.",
          features: ["Electrical Installations", "Fault Finding & Repairs", "DB Board Maintenance", "Lighting Systems", "Emergency Repairs", "Compliance Certificates"]
        },
        {
          name: "Mechanical & Plumbing Services",
          icon: Wrench,
          description: "Expert mechanical and plumbing services for all building systems. From routine maintenance to emergency repairs.",
          features: ["Plumbing Repairs", "Pipe Installation", "Leak Detection", "Geyser Installation", "Drainage Systems", "Pump Maintenance"]
        },
        {
          name: "HVAC Systems",
          icon: Wind,
          description: "Heating, ventilation, and air conditioning services to maintain optimal indoor climate control and air quality.",
          features: ["AC Installation", "AC Repairs & Servicing", "Ventilation Systems", "Temperature Control", "Air Quality Management", "System Optimization"]
        },
        {
          name: "Building Repairs & Construction",
          icon: Hammer,
          description: "Minor construction works, building repairs, and maintenance to keep your facility in optimal condition.",
          features: ["Building Repairs", "Painting Services", "Ceiling Repairs", "Tiling & Flooring", "Door & Window Repairs", "Roofing Maintenance"]
        },
        {
          name: "Preventative Maintenance Programmes",
          icon: FileCheck,
          description: "Scheduled maintenance programs to prevent equipment failure and extend asset lifespan. Reduce downtime and costly repairs.",
          features: ["Scheduled Inspections", "Maintenance Planning", "Asset Management", "Performance Monitoring", "Compliance Tracking", "Reporting Systems"]
        }
      ],
      benefits: [
        "Qualified and certified technicians",
        "Preventative and corrective maintenance",
        "Reduced equipment downtime",
        "Extended asset lifespan",
        "Compliance with safety regulations"
      ],
      color: "from-red-700 to-red-800",
      gradient: "bg-gradient-to-br from-red-50 to-orange-50"
    },
    {
      id: 'facilities',
      icon: Building2,
      title: "Facilities & Support Services",
      shortDescription: "Complete facility management",
      fullDescription: "Our facilities and support services are designed to maintain orderly, functional, and well-managed environments across various sites. We provide flexible service models tailored to client requirements and site-specific conditions.",
      items: [
        {
          name: "Landscaping & Garden Services",
          icon: Leaf,
          description: "Professional landscaping and garden maintenance to enhance the aesthetic appeal and functionality of your outdoor spaces.",
          features: ["Lawn Maintenance", "Garden Design", "Tree Felling & Pruning", "Irrigation Systems", "Seasonal Planting", "Outdoor Beautification"]
        },
        {
          name: "Waste Management Solutions",
          icon: Trash2,
          description: "Comprehensive waste management including collection, recycling, and disposal services. Promoting sustainability and compliance.",
          features: ["Waste Collection", "Recycling Programs", "Bin Management", "Hazardous Waste Handling", "Compliance Reporting", "Skip Hire Services"]
        },
        {
          name: "Janitorial Services",
          icon: BriefcaseIcon,
          description: "Complete janitorial support for daily operations. Maintaining clean, organized, and functional workspaces.",
          features: ["Daily Cleaning", "Restocking Supplies", "Minor Repairs", "Facility Monitoring", "Event Setup", "24/7 Support"]
        },
        {
          name: "Site Support & General Labour",
          icon: Users,
          description: "Flexible labour solutions for various site operations. Skilled and general labour for projects and daily operations.",
          features: ["General Labour", "Loading & Offloading", "Moving Services", "Event Support", "Warehouse Assistance", "Project Support"]
        }
      ],
      benefits: [
        "Flexible service models",
        "Trained and supervised staff",
        "Cost-effective solutions",
        "Site-specific customization",
        "Quality assurance processes"
      ],
      color: "from-gray-700 to-gray-800",
      gradient: "bg-gradient-to-br from-green-50 to-emerald-50"
    },
    {
      id: 'property',
      icon: Home,
      title: "Property & Facilities Management",
      shortDescription: "Full-service property solutions",
      fullDescription: "MH Intelligence offers structured property and facilities management solutions focused on asset preservation, compliance, and tenant satisfaction. Our integrated approach ensures efficient management throughout the property lifecycle.",
      items: [
        {
          name: "Property Management Services",
          icon: Building2,
          description: "Comprehensive property management for residential and commercial properties. From tenant relations to maintenance coordination.",
          features: ["Tenant Management", "Rent Collection", "Maintenance Coordination", "Property Inspections", "Lease Administration", "Financial Reporting"]
        },
        {
          name: "Facilities Coordination",
          icon: Target,
          description: "Coordinated facilities management ensuring all building systems operate efficiently and maintenance is scheduled appropriately.",
          features: ["Maintenance Scheduling", "Vendor Management", "Compliance Monitoring", "Cost Control", "Emergency Response", "Performance Reporting"]
        },
        {
          name: "Residential Housing Construction",
          icon: Home,
          description: "Construction and project management for residential housing developments. From planning to completion.",
          features: ["Project Planning", "Construction Management", "Quality Control", "Budget Management", "Compliance Certification", "Handover Services"]
        }
      ],
      benefits: [
        "Asset preservation and value enhancement",
        "Regulatory compliance management",
        "Tenant satisfaction focus",
        "Cost optimization",
        "Lifecycle property management"
      ],
      color: "from-red-800 to-red-900",
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50"
    }
  ];

  const stats = [
    { number: 100, suffix: "%", label: "Black-Owned", icon: Award },
    { number: 1, prefix: "Level ", label: "B-BBEE Status", icon: Star },
    { number: 2023, prefix: "", label: "Established", icon: Building2 },
    { number: 24, suffix: "/7", label: "Service Available", icon: Clock }
  ];

  const values = ["Professionalism", "Integrity & Ethics", "Accountability", "Innovation", "Efficiency", "Sustainability"];
  const compliance = ["PSIRA Registration", "Level 1 B-BBEE Certified", "PAYE Registered", "CSD Registered", "COIDA Letter of Good Standing", "UIF Letter of Good Standing", "SARS Tax Clearance", "R1M Public Liability Insurance"];

  const clients = [
    { name: "Theresapark Primary School", service: "CCTV & AI Integration" },
    { name: "Bosveld Hurkor Flats", service: "Security Services" },
    { name: "Klerksoord Estate", service: "Security Services" },
    { name: "Department of Water and Sanitation", service: "Security Services" },
    { name: "Rams Offal", service: "Security Services" },
    { name: "Mathithivhale Primary", service: "Security Services" },
    { name: "Doornpoort Primary", service: "Technical Support" }
  ];

  const testimonials = [
    {
      name: "Sechomila Gauta",
      initials: "SG",
      position: "Principal",
      company: "Theresapark Primary School",
      text: "MH Intelligence delivered exceptional CCTV and AI integration services. Their professionalism and technical expertise exceeded our expectations.",
      rating: 5
    },
    {
      name: "Franco",
      initials: "F",
      position: "Manager",
      company: "Bosveld Hurkor Flats",
      text: "Outstanding security services. The team is professional, reliable, and always responsive to our needs.",
      rating: 5
    },
    {
      name: "Muhuliseni Mudau",
      initials: "MM",
      position: "Director",
      company: "Department of Water and Sanitation",
      text: "We've been extremely satisfied with MH Intelligence's security services. Highly recommended for any business.",
      rating: 5
    }
  ];

  const scrollToSection = (id) => {
    if (id.startsWith('service-')) {
      const serviceId = id.replace('service-', '');
      setCurrentServicePage(serviceId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  };

  const goBackToHome = () => {
    setCurrentServicePage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stockImages = {
    logo: '/images/mhi-logo-2.png',
    heroSecurity: 'images/security-pic.jpg',
    heroTechnical: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800&q=80',
    heroCleaning: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80',
    serviceSecurity: 'images/security-pic.jpg',
    serviceCleaning: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80',
    serviceTechnical: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800&q=80',
    serviceFacilities: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&q=80',
    serviceProperty: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80',
    officeTeam: '/images/about-team.png',
  };

  const AnimatedCounter = ({ end, suffix = "", prefix = "", duration = 2000 }) => {
    const [count, setCount] = useState(0);
    const counterRef = useRef(null);
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            
            let start = 0;
            const increment = end / (duration / 16);
            const timer = setInterval(() => {
              start += increment;
              if (start >= end) {
                setCount(end);
                clearInterval(timer);
              } else {
                setCount(Math.floor(start));
              }
            }, 16);
            
            observer.disconnect();
            
            return () => clearInterval(timer);
          }
        },
        { threshold: 0.5 }
      );

      if (counterRef.current && !hasAnimatedRef.current) {
        observer.observe(counterRef.current);
      }

      return () => observer.disconnect();
    }, [end, duration]);

    return (
      <div ref={counterRef} className="text-4xl md:text-5xl font-black text-white mb-2">
        {prefix}{count}{suffix}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-black via-gray-900 to-red-900 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative">
            <img 
              src={stockImages.logo}
              alt="MH Intelligence" 
              className="h-32 w-auto animate-pulse-scale"
            />
          </div>
          <div className="mt-8 text-white text-xl font-bold animate-pulse">
            ...Loading...
          </div>
          <div className="mt-4 flex gap-2 justify-center">
            <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // If viewing a service detail page
  if (currentServicePage) {
    const service = servicesDetailed.find(s => s.id === currentServicePage);
    if (!service) return null;

    const ServiceIcon = service.icon;

    return (
      <div className="bg-white text-gray-900 font-sans">
        {/* Navigation */}
        <nav className="fixed w-full z-40 bg-gradient-to-r from-black via-gray-900 to-red-900 shadow-2xl py-2 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <button onClick={goBackToHome} className="flex items-center group">
                <img 
                  src={stockImages.logo}
                  alt="MH Intelligence" 
                  className="h-14 w-auto group-hover:scale-110 transition-all duration-300"
                />
              </button>
              <button 
                onClick={goBackToHome}
                className="text-white hover:text-red-500 transition-colors font-semibold flex items-center gap-2"
              >
                <ArrowRight className="w-5 h-5 rotate-180" />
                Back to Home
              </button>
            </div>
          </div>
        </nav>

        {/* Service Detail Hero */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24 bg-gradient-to-br from-black via-gray-900 to-red-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 py-20">
            <div className={`w-32 h-32 bg-gradient-to-br ${service.color} rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl transform hover:scale-110 transition-all duration-300`}>
              <ServiceIcon className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {service.fullDescription}
            </p>
          </div>
        </section>

        {/* Service Items Grid */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {service.items.map((item, index) => {
                const ItemIcon = item.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 transform hover:-translate-y-2"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                        <ItemIcon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.name}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h4 className="font-bold text-gray-900 mb-3">Key Features:</h4>
                      {item.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl md:text-5xl font-black text-center text-gray-900 mb-12">
              Why Choose Our <span className="text-red-600">{service.title}</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-red-50 to-gray-50 rounded-xl p-6 border-l-4 border-red-600 hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-800 font-medium">{benefit}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-red-600 to-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Contact us today for a free consultation and quote
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={goBackToHome}
                className="bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transform hover:scale-110 transition-all duration-300 inline-flex items-center justify-center gap-2"
              >
                Get A Quote
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={goBackToHome}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-red-600 transition-all duration-300"
              >
                View All Services
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gradient-to-r from-black via-gray-900 to-red-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-400">© 2023-2025 MH Intelligence (Pty) Ltd. All rights reserved.</p>
          </div>
        </footer>
      </div>
    );
  }

  // Main homepage content
  return (
    <div className="bg-white text-gray-900 font-sans overflow-hidden">
      {/* Navigation */}
      <nav className={`fixed w-full z-40 transition-all duration-700 ${
        isScrolled 
          ? 'bg-gray-900/95 backdrop-blur-md shadow-2xl py-2 border-b border-gray-800' 
          : 'bg-transparent py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`flex justify-between items-center transition-all duration-500 ${
            isScrolled ? 'h-16' : 'h-20'
          }`}>
            <div className="flex items-center group">
              <img 
                src={stockImages.logo}
                alt="MH Intelligence" 
                className={`w-auto transition-all duration-700 ${
                  isScrolled ? 'h-12' : 'h-16'
                } group-hover:scale-110`}
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-1">
              {/* Services Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                  className={`relative px-5 py-2.5 font-semibold transition-all duration-300 rounded-lg ${
                    activeSection === 'services' 
                      ? 'text-red-500' 
                      : 'text-white hover:text-red-400'
                  }`}
                >
                  Services
                  <ChevronDown className={`inline-block w-4 h-4 ml-1 transition-transform duration-300 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {servicesDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-80 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden animate-fade-in">
                    {servicesDetailed.map((service) => {
                      const ServiceIcon = service.icon;
                      return (
                        <button
                          key={service.id}
                          onClick={() => scrollToSection(`service-${service.id}`)}
                          className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-700 transition-all duration-300 border-b border-gray-700 last:border-b-0 group"
                        >
                          <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                            <ServiceIcon className="w-6 h-6 text-white" />
                          </div>
                          <div className="text-left">
                            <div className="font-bold text-white group-hover:text-red-400 transition-colors">{service.title}</div>
                            <div className="text-sm text-gray-400">{service.shortDescription}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {['About', 'Features', 'Contact Us'].map((item) => {
                    const sectionId = item === 'About' ? 'aboutus' :
                                      item === 'Features' ? 'clients' : 'contact';
                    const isActive = activeSection === sectionId;
                    
                    return (
                      <button
                        key={item}
                        onClick={() => scrollToSection(sectionId)}
                        className={`relative px-5 py-2.5 font-semibold transition-all duration-300 rounded-lg ${
                          isActive ? 'text-red-500' : 'text-white hover:text-red-400'
                        }`}
                      >
                        {item}
                      </button>
                    );
                  })}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden relative p-2.5 rounded-lg"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute left-0 top-1 w-6 h-0.5 transition-all duration-300 bg-white ${mobileMenuOpen ? 'rotate-45 top-2.5' : ''}`}></span>
                <span className={`absolute left-0 top-2.5 w-6 h-0.5 transition-all duration-300 bg-white ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`absolute left-0 top-4 w-6 h-0.5 transition-all duration-300 bg-white ${mobileMenuOpen ? '-rotate-45 top-2.5' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden border-t border-gray-800 shadow-2xl transition-all duration-500 overflow-hidden ${
          mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } bg-gray-900`}>
          <div className="px-4 py-6 space-y-2">
            {/* Mobile Services Submenu */}
            <div className="space-y-2">
              <div className="text-sm font-bold text-red-500 px-5 py-2">SERVICES</div>
              {servicesDetailed.map((service) => {
                const ServiceIcon = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => scrollToSection(`service-${service.id}`)}
                    className="w-full px-6 py-3 flex items-center gap-3 hover:bg-gray-800 transition-all duration-300 rounded-lg text-left"
                  >
                    <div className={`w-10 h-10 bg-gradient-to-br ${service.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <ServiceIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-white font-semibold text-sm">{service.title}</div>
                  </button>
                );
              })}
            </div>

            {['About', 'Services', 'Features', 'Contact Us'].map((item) => (
              <button 
                key={item}
                onClick={() => scrollToSection(item === 'About' ? 'aboutus' : 
                                               item === 'Services' ? 'services' :
                                               item === 'Features' ? 'clients' : 'contact')}
                className="block w-full text-left px-5 py-3 rounded-lg font-semibold text-white hover:bg-gray-800 hover:text-red-400 transition-all"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section - Conditional Design */}
      <section className="hero-section relative min-h-screen flex items-center overflow-hidden">
        {currentHero === 0 ? (
          // First Slide - Dark Clean Design
          <>
            {/* Dark gradient background with abstract wave shapes */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800">
              {/* Abstract decorative shapes */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[800px] h-[800px] bg-gradient-to-br from-red-900/20 to-red-800/10 rounded-full blur-3xl"></div>
                <div className="absolute top-1/4 -right-40 w-[1000px] h-[1000px] bg-gradient-to-bl from-red-800/15 to-red-700/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 left-1/4 w-[900px] h-[900px] bg-gradient-to-tr from-red-900/15 to-gray-800/10 rounded-full blur-3xl"></div>
              </div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                {/* Left side - Logo */}
                <div className="flex justify-center md:justify-start order-2 md:order-1">
                  <div className="relative max-w-lg w-full">
                    <img 
                      src={stockImages.logo}
                      alt="MH Intelligence" 
                      className="w-full drop-shadow-2xl "
                    />
                  </div>
                </div>

                {/* Right side - Content */}
                <div className="space-y-8 order-1 md:order-2">
                  <div className="space-y-6">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
                      Protecting, Maintaining, and Powering Your World
                    </h1>
                    <div className="space-y-4 text-gray-300 text-base md:text-lg leading-relaxed">
                    <p>
                      At MH Intelligence, we deliver integrated security, cleaning, technical maintenance, facilities support, and property management solutions to residential, commercial, industrial, and public-sector clients across South Africa. Our services are underpinned by skilled personnel, disciplined management processes, and the strategic use of modern technology to protect assets, enhance operational efficiency, and extend infrastructure lifespan.
                    </p>
                    <p>
                      Guided by professionalism, integrity, and accountability, we build long-term partnerships through compliant, reliable, and value-driven service delivery. At MH Intelligence, we go beyond service provision—offering peace of mind, operational continuity, and sustainable environments that support growth, safety, and community development.
                    </p>
                  </div>
                  </div>
                  <div>
                    <button 
                      onClick={() => scrollToSection('contact')}
                      className="bg-red-600 hover:bg-red-700 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                    >
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Minimal scroll indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce opacity-50">
              <ChevronDown className="w-6 h-6 text-gray-400" />
            </div>
          </>
        ) : (
          // Other Slides - Original Photo Background Design
          <>
            <div className="absolute inset-0">
              <div className="absolute inset-0 transition-opacity duration-1000 ease-in-out">
                <img 
                  src={
                    currentHero === 1 ? stockImages.heroSecurity :
                    currentHero === 2 ? stockImages.heroTechnical :
                    stockImages.heroCleaning
                  }
                  alt={heroSlides[currentHero].title}
                  className={`w-full h-full object-cover transition-all duration-1000 ease-in-out ${
                    imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                  }`}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/70"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-transparent to-black/30 animate-gradient-shift"></div>
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
              <div className="space-y-6">
                <h1 className={`text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-4 leading-tight transition-all duration-700 ${
                  imageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <span className="inline-block">{heroSlides[currentHero].title}</span>
                  <br />
                  <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent inline-block">
                    {heroSlides[currentHero].subtitle}
                  </span>
                </h1>
                
                <p className={`text-lg md:text-xl lg:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed px-4 transition-all duration-700 delay-100 ${
                  imageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  {heroSlides[currentHero].description}
                </p>

                <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center pt-8 transition-all duration-700 delay-200 ${
                  imageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <button 
                    onClick={() => scrollToSection('quote')}
                    className="group bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transform hover:scale-110 transition-all duration-300 flex items-center gap-2"
                  >
                    <span>{heroSlides[currentHero].cta}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </button>
                  <button 
                    onClick={() => scrollToSection('services')}
                    className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
                  >
                    OUR SERVICES
                  </button>
                </div>
              </div>

              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                <ChevronDown className="w-8 h-8 text-white opacity-75" />
              </div>
            </div>
          </>
        )}

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex justify-center gap-3 z-20">
          {heroSlides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentHero(index)}
              className={`group relative transition-all duration-500 ${
                currentHero === index 
                  ? 'w-12 h-2 bg-red-600 rounded-full' 
                  : 'w-8 h-2 bg-gray-400 hover:bg-gray-300 rounded-full'
              }`}
              aria-label={`Go to slide ${index + 1}: ${slide.title}`}
            >
              {/* Tooltip on hover */}
              <span className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs px-3 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                {slide.title}
              </span>
            </button>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => setCurrentHero((prev) => (prev === 0 ? 3 : prev - 1))}
          className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-red-600 text-white p-4 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm group shadow-lg"
          aria-label="Previous slide"
        >
          <ArrowRight className="w-6 h-6 rotate-180 group-hover:scale-110 transition-transform" />
        </button>
        <button
          onClick={() => setCurrentHero((prev) => (prev === 3 ? 0 : prev + 1))}
          className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-red-600 text-white p-4 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm group shadow-lg"
          aria-label="Next slide"
        >
          <ArrowRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </section>

      {/* Stats Bar */}
      <section className="bg-gradient-to-r from-red-600 to-black py-16 relative overflow-hidden" data-animate="stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const isVisible = visibleSections.has('stats');
              return (
                <div 
                  key={index} 
                  className={`text-center group transform transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <Icon className="w-10 h-10 mx-auto mb-3 text-white opacity-80" />
                  <AnimatedCounter 
                    end={stat.number} 
                    suffix={stat.suffix || ""} 
                    prefix={stat.prefix || ""}
                  />
                  <div className="text-sm md:text-base text-gray-200 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section id="services" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black" data-animate="services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleSections.has('services') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-block">
              <div className="text-sm font-bold text-red-500 tracking-widest mb-2">OUR EXPERTISE</div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                COMPREHENSIVE <span className="text-red-500">SOLUTIONS</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto"></div>
            </div>
            <p className="mt-6 text-xl text-gray-300 max-w-3xl mx-auto">
              Five integrated service divisions delivering excellence across all sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesDetailed.map((service, index) => {
              const Icon = service.icon;
              const isVisible = visibleSections.has('services');
              return (
                <div
                  key={service.id}
                  className={`group relative bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-4 cursor-pointer border border-gray-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                  onClick={() => scrollToSection(`service-${service.id}`)}
                >
                  <div className={`absolute inset-0 ${service.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                  
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={
                        index === 0 ? stockImages.serviceSecurity :
                        index === 1 ? stockImages.serviceCleaning :
                        index === 2 ? stockImages.serviceTechnical :
                        index === 3 ? stockImages.serviceFacilities :
                        stockImages.serviceProperty
                      }
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center shadow-lg">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 relative z-10">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-4 text-sm">
                      {service.shortDescription}
                    </p>

                    <button className="text-red-500 font-semibold flex items-center gap-2 group-hover:text-red-400">
                      View Details
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="aboutus" className="py-24 bg-gradient-to-br from-black via-gray-900 to-gray-800" data-animate="about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={`relative transition-all duration-1000 ${visibleSections.has('about') ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={stockImages.officeTeam}
                  alt="MH Intelligence Office"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            </div>

            <div className={`transition-all duration-1000 ${visibleSections.has('about') ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
              <div className="inline-block mb-6">
                <div className="text-sm font-bold text-red-500 tracking-widest mb-2">SINCE 2023</div>
                <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                  ABOUT
                  <span className="block text-red-500">MH INTELLIGENCE</span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400"></div>
              </div>

              <div className="space-y-6 text-gray-300">
                <p className="text-lg font-semibold text-white">
                  Maphangule Holdings (Pty) Ltd, trading as MH Intelligence
                </p>
                <p>
                  A 100% Black-owned South African company providing integrated security, cleaning, technical maintenance, facilities support, and property management solutions. Founded by Mr. Maphangule Rudzani Eldriedge in 2023.
                </p>
                <p>
                  We service commercial buildings, industrial plants, residential estates, schools, communities, and public facilities. Our business philosophy is built on quality service delivery, strong governance, and long-term partnerships.
                </p>
                
                <div className="bg-gradient-to-r from-red-900/30 to-gray-800/30 p-6 rounded-xl border-l-4 border-red-600">
                  <p className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-red-500" />
                    Our Mission
                  </p>
                  <p className="text-gray-300">
                    To protect, maintain, and enhance our clients' assets and environments by delivering professional, compliant, and integrated services that meet the highest industry standards.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {values.slice(0, 4).map((value, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-800 border border-gray-700 p-3 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-red-500" />
                      <span className="font-semibold text-sm text-gray-200">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => scrollToSection('contact')}
                className="mt-8 bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-bold hover:shadow-2xl transform hover:scale-110 transition-all duration-300 inline-flex items-center gap-2"
              >
                Contact Us Today
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-gradient-to-br from-black via-gray-900 to-gray-800" data-animate="compliance">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-1000 ${visibleSections.has('compliance') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-block">
              <div className="text-sm font-bold text-red-500 tracking-widest mb-2">FULLY COMPLIANT</div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                TRAINING, COMPLIANCE & <span className="text-red-500">ACCREDITATION</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {compliance.map((item, index) => {
              const isVisible = visibleSections.has('compliance');
              return (
                <div 
                  key={index} 
                  className={`bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:border-red-600 transition-all duration-500 text-center transform hover:-translate-y-2 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.05}s` }}
                >
                  <FileCheck className="w-10 h-10 text-red-500 mx-auto mb-4" />
                  <div className="font-semibold text-gray-200">{item}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-br from-gray-100 to-white" data-animate="testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleSections.has('testimonials') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-block">
              <div className="text-sm font-bold text-red-600 tracking-widest mb-2">CLIENT TESTIMONIALS</div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
                WHAT OUR <span className="text-red-600">CLIENTS SAY</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-black mx-auto"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => {
              const isVisible = visibleSections.has('testimonials');
              return (
                <div 
                  key={index} 
                  className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-gray-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                      {testimonial.initials}
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{testimonial.name}</h4>
                      <p className="text-sm text-gray-400">{testimonial.position}</p>
                      <p className="text-sm text-red-400 font-medium">{testimonial.company}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-red-500 text-red-500" />
                    ))}
                  </div>
                  
                  <p className="text-gray-300 italic">
                    "{testimonial.text}"
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black" data-animate="clients">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleSections.has('clients') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-block">
              <div className="text-sm font-bold text-red-500 tracking-widest mb-2">TRUSTED BY</div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                OUR <span className="text-red-500">CLIENTS</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((client, index) => {
              const isVisible = visibleSections.has('clients');
              return (
                <div 
                  key={index} 
                  className={`bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:border-red-600 border-l-4 transform hover:-translate-y-2 transition-all duration-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-white text-lg mb-1">{client.name}</h3>
                      <p className="text-gray-400 text-sm">{client.service}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote" className="py-24 bg-gradient-to-br from-black via-gray-900 to-red-900 relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              GET YOUR <span className="text-red-500">FREE QUOTE</span>
            </h2>
            <p className="text-xl text-gray-300">
              Let us know your requirements and we'll provide a customized solution
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Full Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none"
                    placeholder="Your Company"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Email Address <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Phone Number <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none"
                    placeholder="+27 XX XXX XXXX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Service Required <span className="text-red-600">*</span>
                </label>
                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none">
                  <option>Select a service...</option>
                  {servicesDetailed.map((service) => (
                    <option key={service.id}>{service.title}</option>
                  ))}
                  <option>Multiple Services</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Message / Requirements <span className="text-red-600">*</span>
                </label>
                <textarea
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-600 focus:outline-none resize-none"
                  placeholder="Tell us about your specific requirements..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                Submit Quote Request
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black" data-animate="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleSections.has('contact') ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              GET IN <span className="text-red-500">TOUCH</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Phone, title: "Call Us", details: ["012 320 3752", "069 732 1465"], color: "from-red-600 to-red-700" },
              { icon: Mail, title: "Email Us", details: ["sales@mhintelligence.co.za"], color: "from-black to-gray-800" },
              { icon: MapPin, title: "Visit Us", details: ["24 Leopard Road", "Theresapark, Pretoria North", "Akasia, Gauteng, 0200"], color: "from-red-700 to-red-800" }
            ].map((contact, index) => {
              const Icon = contact.icon;
              const isVisible = visibleSections.has('contact');
              return (
                <div 
                  key={index} 
                  className={`bg-gray-800 border border-gray-700 rounded-2xl shadow-lg hover:shadow-2xl hover:border-red-600 transition-all duration-500 p-8 text-center transform hover:-translate-y-4 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${contact.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{contact.title}</h3>
                  {contact.details.map((detail, i) => (
                    <p key={i} className="text-gray-400 mb-1">{detail}</p>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-black via-gray-900 to-red-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <img 
                src={stockImages.logo}
                alt="MH Intelligence" 
                className="h-20 w-auto mb-4"
              />
              <p className="text-gray-400 mb-4">
                100% Black-owned, Level 1 B-BBEE integrated facility management solutions provider.
              </p>
              <div className="space-y-1 text-sm text-gray-400">
                <p>Company Registration: 2023/609955/07</p>
                <p>EST. 2023</p>
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-lg">Quick Links</h3>
              <ul className="space-y-2">
                {['Services', 'About Us', 'Clients', 'Contact'].map((link) => (
                  <li key={link}>
                    <button 
                      onClick={() => scrollToSection(link.toLowerCase().replace(' ', ''))}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-lg">Our Services</h3>
              <ul className="space-y-2 text-sm">
                {servicesDetailed.map((service) => (
                  <li key={service.id}>
                    <button 
                      onClick={() => scrollToSection(`service-${service.id}`)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      {service.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-700 text-center">
            <p className="text-gray-400 text-sm">
              © 2023-2025 MH Intelligence (Pty) Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Action Button */}
      <button
        onClick={() => scrollToSection('quote')}
        className="fixed bottom-8 right-8 bg-gradient-to-r from-red-600 to-red-700 text-white p-4 rounded-full shadow-2xl hover:scale-125 transition-all duration-300 z-40 animate-bounce-slow"
      >
        <Phone className="w-6 h-6" />
      </button>

      {/* Scroll to Top */}
      {isScrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-8 bg-gray-900 text-white p-3 rounded-full shadow-xl hover:scale-110 transition-all duration-300 z-40"
        >
          <ChevronDown className="w-5 h-5 transform rotate-180" />
        </button>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        * {
          font-family: 'Inter', sans-serif;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .animate-fade-in { animation: fade-in 1s ease-out; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-scale { animation: pulse-scale 3s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-gradient-shift { 
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }

        html {
          scroll-behavior: smooth;
        }

        ::-webkit-scrollbar {
          width: 12px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #dc2626, #000000);
          border-radius: 6px;
        }
      `}</style>
    </div>
  );
};

export default MHIntelligence;